import { formatMessageWithLocale, formatLocalizedString, LOCALE } from '../../i18n/i18n';
import ComparisonItem from '../../../types/comparison-item';
import { SampledSeriesData, ScatterSeriesData, ChartType, BarPoint, BarSeries, CHART_CONFIG } from './types';
import { formatters } from './chart-formatters';
import { isDrawdownChart } from './chart-validators';
import { Locale } from '@capintel/capintel-instruments-lib';

export const getLocaleEnum = (locale: string): Locale => {
  switch (locale) {
    case 'fr-CA':
      return LOCALE.FR_CA;
    case 'en-US':
      return LOCALE.EN_US;
    case 'en-CA':
    default:
      return LOCALE.EN_CA;
  }
};

export const getA11yText = (key: string, locale: string, values?: Record<string, any>): string => {
  const targetLocale = getLocaleEnum(locale);

  try {
    return formatMessageWithLocale(key, targetLocale, values) || key;
  } catch (error) {
    console.warn(`Failed to get localized text for key: ${key}`, error, 'Values:', values);
    return key;
  }
};

const hasNoSeries = (series: any[]): boolean => !series || !series.length;

const hasNoData = (series: any[]): boolean => {
  return hasNoSeries(series) || !series.some(s => s.data && s.data.length > 0);
};

const getFirstSeriesWithData = (series: any[]) => series.find(s => s.data && s.data.length > 0);

const getTotalPointCount = (series: any[]): number => series.reduce((sum, s) => sum + (s.data?.length || 0), 0);

const getEmptyText = (chartType: ChartType, locale: string): string => getA11yText(`Charts.AltText.${getChartTypeKey(chartType)}.Empty`, locale);

const getNoDataText = (chartType: ChartType, locale: string): string => getA11yText(`Charts.AltText.${getChartTypeKey(chartType)}.NoData`, locale);

const handleChartError = (error: any, chartType: ChartType, locale: string): string => {
  console.warn(`Error building ${chartType} chart:`, error);
  return getEmptyText(chartType, locale);
};

const getDateRange = (series: any, locale: string) => {
  const firstData = series.data[0];
  const lastData = series.data[series.data.length - 1];

  return {
    startDate: formatters.date(firstData.timestamp, locale, false),
    endDate: formatters.date(lastData.timestamp, locale, false),
  };
};

const joinDescriptions = (descriptions: string[], locale: string): string => {
  if (descriptions.length === 1) return descriptions[0];

  const connector = getA11yText('Charts.AltText.LineChart.And', locale);
  const separator = ', ';

  if (descriptions.length === 2) {
    return descriptions.join(` ${connector} `);
  }

  const lastItem = descriptions[descriptions.length - 1];
  const beforeLast = descriptions.slice(0, -1).join(separator);

  return `${beforeLast}${separator}${connector} ${lastItem}`;
};

const getChartTypeKey = (chartType: ChartType): string => {
  const typeMap = {
    line: 'LineChart',
    scatter: 'ScatterPlot',
    drawdown: 'DrawdownChart',
    bar: 'BarChart',
    donut: 'DonutChart',
  };
  return typeMap[chartType] || 'LineChart';
};

export const buildTitle = (
  sampledSeries: SampledSeriesData[],
  locale: string,
  chartType: ChartType = 'line',
  scenarioName?: string,
  scenarioPeriod?: { startDate: string; endDate: string },
): string => {
  try {
    if (hasNoSeries(sampledSeries)) return getEmptyText(chartType, locale);

    const firstSeriesWithData = getFirstSeriesWithData(sampledSeries);
    if (!firstSeriesWithData) return getNoDataText(chartType, locale);

    const { startDate, endDate } = getDateRange(firstSeriesWithData, locale);

    if (scenarioName) {
      return getA11yText(`Charts.AltText.${getChartTypeKey(chartType)}.HistoricalTitle`, locale, {
        seriesCount: sampledSeries.length,
        scenarioLabel: scenarioName,
        startDate: scenarioPeriod?.startDate || startDate,
        endDate: scenarioPeriod?.endDate || endDate,
        chartStartDate: startDate,
        chartEndDate: endDate,
      });
    }

    return getA11yText(`Charts.AltText.${getChartTypeKey(chartType)}.Title`, locale, {
      seriesCount: sampledSeries.length,
      startDate,
      endDate,
    });
  } catch (error) {
    return handleChartError(error, chartType, locale);
  }
};

export const buildAltText = (sampledSeries: SampledSeriesData[], locale: string, proposalItems: ComparisonItem[]): string => {
  if (hasNoData(sampledSeries)) return getNoDataText('line', locale);

  const isDrawdown = isDrawdownChart(sampledSeries);

  const formatValue = (value: number, currency?: string): string => {
    return isDrawdown ? formatters.percentage(value, locale) : formatters.currency(value, locale, currency);
  };

  const seriesTexts = sampledSeries.map((series, seriesIndex) => {
    if (!series.data.length) {
      return getA11yText('Charts.AltText.LineChart.NoDataPoints', locale, {
        seriesName: series.name,
      });
    }

    const seriesCurrency = proposalItems[seriesIndex]?.currency;

    const pointDescriptions = series.data.map(p => {
      const localizedDate = formatters.date(p.timestamp, locale);
      const localizedValue = formatValue(p.value, seriesCurrency);

      return getA11yText('Charts.AltText.LineChart.DataPoint', locale, {
        value: localizedValue,
        date: localizedDate,
      });
    });

    const joined = joinDescriptions(pointDescriptions, locale);
    const chartTypeKey = isDrawdown ? 'DrawdownChart' : 'LineChart';
    const showsText = getA11yText(`Charts.AltText.${chartTypeKey}.Shows`, locale);

    return `${series.name} ${showsText} ${joined}.`;
  });

  return seriesTexts.join(' ');
};

export const buildScatterTitle = (sampledSeries: ScatterSeriesData[], locale: string, periodLabel?: string | null): string => {
  try {
    if (hasNoSeries(sampledSeries)) return getEmptyText('scatter', locale);

    const totalPoints = getTotalPointCount(sampledSeries);
    if (totalPoints === 0) return getNoDataText('scatter', locale);

    let title = getA11yText('Charts.AltText.ScatterPlot.Title', locale, {
      seriesCount: sampledSeries.length,
      totalPoints,
    });

    if (periodLabel) {
      const periodText = getA11yText('Charts.AltText.ScatterPlot.ForPeriod', locale, {
        periodLabel,
      });
      title += ` ${periodText}`;
    }

    return `${title}.`;
  } catch (error) {
    return handleChartError(error, 'scatter', locale);
  }
};

export const buildScatterAltText = (sampledSeries: ScatterSeriesData[], locale: string): string => {
  if (hasNoData(sampledSeries)) return getNoDataText('scatter', locale);

  const seriesTexts = sampledSeries.map(series => {
    if (!series.data.length) {
      return getA11yText('Charts.AltText.ScatterPlot.NoDataPoints', locale, {
        seriesName: series.name,
      });
    }

    const pointDescriptions = series.data.map(point => {
      const stdDev = formatters.decimal(point.x, locale);
      const returnValue = formatters.percentage(point.y, locale);

      return getA11yText('Charts.AltText.ScatterPlot.DataPoint', locale, {
        stdDev,
        return: returnValue,
      });
    });

    const joined = joinDescriptions(pointDescriptions, locale);
    const showsText = getA11yText('Charts.AltText.LineChart.Shows', locale);

    return `${series.name} ${showsText} ${joined}.`;
  });

  return seriesTexts.join(' ');
};

// DONUT CHART BUILDERS
export const buildDonutTitle = (donutData: any, locale: string): string => {
  try {
    if (!donutData || !donutData.donutHeader) {
      return getNoDataText('donut', locale);
    }

    const { donutHeader } = donutData;
    const portfolioName = donutHeader.name || null;

    if (!portfolioName) {
      return getA11yText('Charts.AltText.DonutChart.NoHeader', locale);
    }
    return getA11yText('Charts.AltText.DonutChart.Title', locale, {
      portfolioName,
      portfolioType: donutHeader.type || 'portfolio',
    });
  } catch (error) {
    return handleChartError(error, 'donut', locale);
  }
};

export const buildDonutAltText = (donutData: any, locale: string): string => {
  try {
    if (!donutData || !donutData.data || !Array.isArray(donutData.data) || donutData.data.length === 0) {
      return getNoDataText('donut', locale);
    }

    const segments = donutData.data[0];

    if (!Array.isArray(segments) || segments.length === 0) {
      return getNoDataText('donut', locale);
    }

    const significantSegments = segments
      .filter(segment => (segment.y || 0) >= CHART_CONFIG.DONUT_MIN_SIGNIFICANT_PERCENTAGE)
      .sort((a, b) => (b.y || 0) - (a.y || 0));

    if (significantSegments.length === 0) {
      return getNoDataText('donut', locale);
    }

    const segmentDescriptions = significantSegments.map(segment => {
      const name = segment.name || getA11yText('Charts.AltText.Common.UntitledSegment', locale);
      const percentage = formatters.percentage(segment.y || 0, locale, true);

      return getA11yText('Charts.AltText.DonutChart.Segment', locale, {
        segmentName: name,
        percentage,
      });
    });

    const joined = joinDescriptions(segmentDescriptions, locale);

    return `${joined}.`;
  } catch (error) {
    console.warn('Error building donut chart alt text:', error);
    return getNoDataText('donut', locale);
  }
};

// BAR CHART BUILDERS
const createChartHelpers = <T>(dataExtractor: (item: T) => any[]) => ({
  hasNoData: (data: T[]): boolean => {
    return (
      !data ||
      data.length === 0 ||
      !data.some(item => {
        const extracted = dataExtractor(item);
        return Array.isArray(extracted) && extracted.length > 0;
      })
    );
  },

  getTotalPointCount: (data: T[]): number => {
    return data.reduce((sum, item) => {
      const extracted = dataExtractor(item);
      return sum + (Array.isArray(extracted) ? extracted.length : 0);
    }, 0);
  },

  getFirstWithData: (data: T[]) => {
    return data.find(item => {
      const extracted = dataExtractor(item);
      return Array.isArray(extracted) && extracted.length > 0;
    });
  },
});

const barHelpers = createChartHelpers((series: any) => series);

export const buildBarTitle = (barData: any[], proposalItems: any[], locale: string): string => {
  try {
    if (barHelpers.hasNoData(barData)) {
      return getNoDataText('bar', locale);
    }

    const seriesCount = barData.length;
    const totalDataPoints = barHelpers.getTotalPointCount(barData);

    if (totalDataPoints === 0) {
      return getNoDataText('bar', locale);
    }

    const firstSeries = barHelpers.getFirstWithData(barData);
    if (firstSeries && firstSeries.length > 0) {
      const years: string[] = firstSeries.map((point: { name: string }) => point.name).sort();
      const periodCount = years.length;
      const firstPeriod = years[0];
      const lastPeriod = years[years.length - 1];

      return (
        getA11yText('Charts.AltText.BarChart.Title', locale, {
          seriesCount,
          periodCount,
          firstPeriod,
          lastPeriod,
        }) + '.'
      );
    }

    return getA11yText('Charts.AltText.BarChart.SimpleTitle', locale, { seriesCount });
  } catch (error) {
    return handleChartError(error, 'bar', locale);
  }
};

export const buildBarAltText = (barData: any[], proposalItems: any[], locale: string): string => {
  try {
    if (barHelpers.hasNoData(barData)) {
      return getNoDataText('bar', locale);
    }

    const firstSeries = barHelpers.getFirstWithData(barData);
    if (!firstSeries || firstSeries.length === 0) {
      return getNoDataText('bar', locale);
    }

    const seriesLabels: string[] = [];
    const seriesDescriptions: string[] = [];

    barData.forEach((series, seriesIndex) => {
      if (Array.isArray(series) && series.length > 0) {
        const label = String.fromCharCode(CHART_CONFIG.BAR_SERIES_LABEL_START_CODE + seriesIndex); // A, B, C, D...
        const itemName = proposalItems[seriesIndex]?.name
          ? formatLocalizedString(proposalItems[seriesIndex].name, getLocaleEnum(locale))
          : getA11yText('Charts.AltText.Common.UntitledSeries', locale);

        seriesLabels.push(label);
        seriesDescriptions.push(
          getA11yText('Charts.AltText.BarChart.SeriesDefinition', locale, {
            label: `Series ${label}`,
            seriesName: itemName,
          }),
        );
      }
    });

    const periods = firstSeries.map((point: { name: string }) => point.name);

    const periodTexts: string[] = periods
      .map((period: string) => {
        const seriesValuesForPeriod: string[] = [];

        barData.forEach((series: BarSeries, seriesIndex: number) => {
          if (!Array.isArray(series) || series.length === 0) return;

          const periodData: BarPoint | undefined = series.find((point: BarPoint) => point.name === period);
          if (periodData) {
            const label: string = String.fromCharCode(65 + seriesIndex); // A, B, C, D...
            const value: string = formatters.percentage(periodData.y || 0, locale, true);

            seriesValuesForPeriod.push(
              getA11yText('Charts.AltText.BarChart.LabeledValue', locale, {
                label,
                value,
              }),
            );
          }
        });

        if (seriesValuesForPeriod.length > 0) {
          const joined: string = seriesValuesForPeriod.join(', ');
          return (
            getA11yText('Charts.AltText.BarChart.PeriodWithLabels', locale, {
              period,
              values: joined,
            }) + '.'
          );
        }

        return null;
      })
      .filter(Boolean) as string[];

    const seriesDefinitionsText = seriesDescriptions.join(' ');
    const periodDataText = periodTexts.join(' ');

    return `${seriesDefinitionsText} ${periodDataText}`;
  } catch (error) {
    console.warn('Error building bar chart alt text:', error);
    return getNoDataText('bar', locale);
  }
};
